// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * API client for Admin Email Notification endpoints.
 * Provides access to notification monitoring, statistics, and retry functionality.
 */

import {
  EmailNotification,
  EmailNotificationStats,
  DailyStats,
  BrokerageStats,
  NotificationFilters,
  PaginationParams,
  PageResponse,
  NotificationType
} from '../types/emailNotifications';

const API_BASE = '/api/admin/email-notifications';

/**
 * Build query string from filters and pagination params.
 */
function buildQueryString(
  filters?: NotificationFilters,
  pagination?: PaginationParams
): string {
  const params = new URLSearchParams();

  if (filters?.brokerageId) {
    params.append('brokerageId', filters.brokerageId);
  }
  if (filters?.status) {
    params.append('status', filters.status);
  }
  if (filters?.notificationType) {
    params.append('notificationType', filters.notificationType);
  }
  if (filters?.startDate) {
    params.append('startDate', filters.startDate);
  }
  if (filters?.endDate) {
    params.append('endDate', filters.endDate);
  }
  if (pagination?.page !== undefined) {
    params.append('page', String(pagination.page));
  }
  if (pagination?.size !== undefined) {
    params.append('size', String(pagination.size));
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Email Notifications Admin API client.
 */
export const emailNotificationsAdminApi = {
  /**
   * List email notifications with filtering and pagination.
   */
  async listNotifications(
    filters?: NotificationFilters,
    pagination?: PaginationParams
  ): Promise<PageResponse<EmailNotification>> {
    const queryString = buildQueryString(filters, pagination);
    const response = await fetch(`${API_BASE}${queryString}`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Failed to fetch notifications');
    }

    return response.json();
  },

  /**
   * Get notification by ID.
   */
  async getNotification(id: string): Promise<EmailNotification> {
    const response = await fetch(`${API_BASE}/${id}`, {
      credentials: 'include'
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Notification not found');
      }
      throw new Error('Failed to fetch notification');
    }

    return response.json();
  },

  /**
   * Get overall email notification statistics.
   */
  async getOverallStats(): Promise<EmailNotificationStats> {
    const response = await fetch(`${API_BASE}/stats`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Failed to fetch statistics');
    }

    return response.json();
  },

  /**
   * Get daily statistics for date range (defaults to last 30 days).
   */
  async getDailyStats(
    startDate?: string,
    endDate?: string
  ): Promise<DailyStats[]> {
    const params = new URLSearchParams();
    if (startDate) {
      params.append('startDate', startDate);
    }
    if (endDate) {
      params.append('endDate', endDate);
    }

    const queryString = params.toString();
    const suffix = queryString ? `?${queryString}` : '';
    const url = `${API_BASE}/stats/daily${suffix}`;

    const response = await fetch(url, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Failed to fetch daily statistics');
    }

    return response.json();
  },

  /**
   * Get statistics grouped by brokerage.
   */
  async getBrokerageStats(): Promise<BrokerageStats[]> {
    const response = await fetch(`${API_BASE}/stats/brokerage`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Failed to fetch brokerage statistics');
    }

    return response.json();
  },

  /**
   * Get statistics grouped by notification type.
   */
  async getTypeStats(): Promise<Record<NotificationType, number>> {
    const response = await fetch(`${API_BASE}/stats/type`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Failed to fetch type statistics');
    }

    return response.json();
  },

  /**
   * Manually retry a failed or scheduled notification.
   */
  async retryNotification(id: string): Promise<EmailNotification> {
    const response = await fetch(`${API_BASE}/${id}/retry`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Notification not found');
      }
      if (response.status === 400) {
        const errorText = await response.text();
        throw new Error(errorText || 'Cannot retry notification');
      }
      throw new Error('Failed to retry notification');
    }

    return response.json();
  }
};
